import { useState, useEffect } from "react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import {
  today,
  currentBusinessDate,
  addMonths,
  subMonths,
  subDays,
  subWeeks,
  subYears,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  endOfDay,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  eachDayOfInterval,
  formatDate,
} from "../../utils/timezone"
import { formatBusinessHoursLabel, getLocationHours } from "../../utils/locationHours"

const predefinedRanges = [
  { label: "Today", key: "today" },
  { label: "Yesterday", key: "yesterday" },
  { label: "This Week", key: "thisWeek" },
  { label: "Last Week", key: "lastWeek" },
  { label: "This Month", key: "thisMonth" },
  { label: "Last Month", key: "lastMonth" },
  { label: "This Year", key: "thisYear" },
  { label: "Last Year", key: "lastYear" },
  { label: "Custom Range", key: "custom" },
]

const getPredefinedRange = (range, businessToday) => {
  switch (range) {
    case "today":
      return { start: businessToday, end: endOfDay(businessToday) }
    case "yesterday": {
      const yesterday = subDays(businessToday, 1)
      return { start: yesterday, end: endOfDay(yesterday) }
    }
    case "thisWeek":
      return { start: startOfWeek(businessToday), end: endOfWeek(businessToday) }
    case "lastWeek": {
      const lastWeek = subWeeks(businessToday, 1)
      return { start: startOfWeek(lastWeek), end: endOfWeek(lastWeek) }
    }
    case "thisMonth":
      return { start: startOfMonth(businessToday), end: endOfMonth(businessToday) }
    case "lastMonth": {
      const lastMonth = subMonths(businessToday, 1)
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) }
    }
    case "thisYear":
      return { start: startOfYear(businessToday), end: endOfYear(businessToday) }
    case "lastYear": {
      const lastYear = subYears(businessToday, 1)
      return { start: startOfYear(lastYear), end: endOfYear(lastYear) }
    }
    default:
      return { start: businessToday, end: businessToday }
  }
}

export default function DateRangePicker({
  isOpen = true,
  onClose = () => {},
  onApply = () => {},
  initialStartDate,
  initialEndDate,
  initialSelectedRange = "today",
}) {
  const [currentMonth, setCurrentMonth] = useState(today())
  const [selectedRange, setSelectedRange] = useState(initialSelectedRange)
  const [startDate, setStartDate] = useState(initialStartDate || currentBusinessDate())
  const [endDate, setEndDate] = useState(initialEndDate || currentBusinessDate())
  const [hoverDate, setHoverDate] = useState(null)
  const [isSelectingEnd, setIsSelectingEnd] = useState(false)
  const locationHours = getLocationHours()
  const businessToday = currentBusinessDate()

  const nextMonth = addMonths(currentMonth, 1)

  useEffect(() => {
    if (selectedRange !== "custom") {
      const { start, end } = getPredefinedRange(selectedRange, currentBusinessDate())
      setStartDate(start)
      setEndDate(end)
    }
  }, [selectedRange])

  const handleDateClick = (date) => {
    if (selectedRange !== "custom") {
      setSelectedRange("custom")
    }

    if (!startDate || (startDate && endDate && !isSelectingEnd)) {
      setStartDate(date)
      setEndDate(null)
      setIsSelectingEnd(true)
    } else if (startDate && !endDate) {
      if (date < startDate) {
        setEndDate(startDate)
        setStartDate(date)
      } else {
        setEndDate(date)
      }
      setIsSelectingEnd(false)
    }
  }

  const handleRangeSelect = (range) => {
    setSelectedRange(range)
    if (range === "custom") {
      setStartDate(null)
      setEndDate(null)
      setIsSelectingEnd(false)
    }
  }

  const handleApply = () => {
    if (startDate && endDate) {
      const label = predefinedRanges.find((r) => r.key === selectedRange)?.label || "Custom Range"
      onApply(startDate, endDate, label)
      onClose()
    }
  }

  const renderCalendar = (month) => {
    const monthStart = startOfMonth(month)
    const monthEnd = endOfMonth(month)
    const calendarStart = startOfWeek(monthStart)
    const calendarEnd = endOfWeek(monthEnd)

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">{formatDate(month, "MMM yyyy")}</h3>
          {month.getTime() === currentMonth.getTime() && (
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 p-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            const isCurrentMonth = isSameMonth(day, month)
            const isBusinessToday = isSameDay(day, businessToday)
            const isSelected = startDate && endDate && isWithinInterval(day, { start: startDate, end: endDate })
            const isStart = startDate && isSameDay(day, startDate)
            const isEnd = endDate && isSameDay(day, endDate)
            const isInHoverRange =
              startDate &&
              hoverDate &&
              !endDate &&
              isWithinInterval(day, {
                start: startDate < hoverDate ? startDate : hoverDate,
                end: startDate < hoverDate ? hoverDate : startDate,
              })

            return (
              <button
                key={index}
                onClick={() => handleDateClick(day)}
                onMouseEnter={() => setHoverDate(day)}
                onMouseLeave={() => setHoverDate(null)}
                className={`
                  h-8 w-8 text-sm rounded flex items-center justify-center transition-colors
                  ${!isCurrentMonth ? "text-gray-300" : "text-gray-900"}
                  ${isSelected || isInHoverRange ? "bg-red-100" : ""}
                  ${isStart || isEnd ? "bg-red-500 text-white" : ""}
                  ${isBusinessToday && !isStart && !isEnd ? "ring-1 ring-blue-500" : ""}
                  ${isCurrentMonth && !isSelected && !isStart && !isEnd ? "hover:bg-gray-100" : ""}
                `.trim()}
              >
                {formatDate(day, "d")}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${formatDate(startDate, "MM/dd/yyyy")} - ${formatDate(endDate, "MM/dd/yyyy")}`
    }
    return ""
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-2 sm:mx-4 max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex flex-col md:flex-row h-full max-h-[92vh]">
          <div className="w-full md:w-48 bg-gray-50 border-b md:border-b-0 md:border-r">
            <div className="p-3 md:p-4 overflow-x-auto">
              <div className="flex md:block gap-2 md:gap-0 min-w-max md:min-w-0">
              {predefinedRanges.map((range) => (
                <button
                  key={range.key}
                  onClick={() => handleRangeSelect(range.key)}
                  className={`
                    whitespace-nowrap md:w-full text-left px-3 py-2 text-sm rounded md:mb-1 transition-colors
                    ${
                      selectedRange === range.key
                        ? range.key === "custom"
                          ? "bg-yellow-400 text-black font-medium"
                          : "bg-blue-100 text-blue-900"
                        : "hover:bg-gray-100"
                    }
                  `.trim()}
                >
                  {range.label}
                </button>
              ))}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-3 sm:p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Select Date Range</h2>
                <p className="mt-1 text-xs text-gray-500">
                  {locationHours.label} business day (CT): {formatBusinessHoursLabel()} next day
                </p>
              </div>
              <button
                onClick={onClose}
                className="h-6 w-6 flex items-center justify-center hover:bg-gray-100 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col md:flex-row">
              {renderCalendar(currentMonth)}
              <div className="hidden md:block">
                {renderCalendar(nextMonth)}
              </div>
            </div>

            <div className="p-3 sm:p-4 border-t bg-gray-50 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
              <input
                value={formatDateRange()}
                readOnly
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm sm:text-base"
                placeholder="Select date range"
              />
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={onClose}
                  className="flex-1 sm:flex-none px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={!startDate || !endDate}
                  className="flex-1 sm:flex-none px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-black rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
